"use client"

import * as React from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog"
import { Button } from "../../components/ui/button"
import { Checkbox } from "../../components/ui/checkbox"
import { Label } from "../../components/ui/label"
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from "../../components/ui/select"
import { Separator } from "../../components/ui/separator"
import { ScrollArea } from "../../components/ui/scroll-area"
import { Input } from "../../components/ui/input"
import { supbaseManager, getUserId } from "../../API"
import { supabase } from "../../supabaseClient"
import { Copy, Check, Upload, X } from "lucide-react"

export type OptionMode = "view_only" | "full_customization" | "custom_experience" | "approval"
export type VisibilityMode = "public" | "private"
export type MainColorTable = string;

export interface UserInfo {
  name: string
  phone: string
}

export interface ColorsOptions {
  editing: boolean
  camisa: string
  gola: string
  manga: string
  calcao: string
  meia: string
}

export interface TextOptions {
  editing: boolean
  add: boolean
  content: boolean
  textColor: boolean
  tableTextColor: string
  borderEditing: boolean
  borderColor: boolean
  tableTextBorderColor: string
  size: boolean
  font: boolean
  rotation: boolean
  delete: boolean
}

export interface LogosOptions {
  editing: boolean
  colorTable: string
  add: boolean
  replace: boolean
  scale: boolean
  rotation: boolean
  delete: boolean
}

export interface LayersOptions {
  editing: boolean
  colorTable: string
  simulator: boolean
  scale: boolean
  rotation: boolean
}

export interface PlusOptions {
  editing: boolean
  clear: boolean
}

export interface PiecesVisibilityOptions {
  avatar: boolean
  camisa: boolean
  short: boolean
  meia: boolean
}

export interface OptionsConfig {
  mode: OptionMode
  visibility: VisibilityMode
  mainColorTable: MainColorTable
  userInfo: UserInfo
  colors: ColorsOptions
  texts: TextOptions
  logos: LogosOptions
  layers: LayersOptions
  plus: PlusOptions
  piecesVisibility: PiecesVisibilityOptions
  landing?: boolean
  approvalClientName?: string
  approvalClientEmail?: string
  customTitle?: string
  customSubtitle?: string
  bgColor?: string
  bgImageUrl?: string
  logoUrl?: string
  cardColor?: string
  showFabrics?: boolean
  showAnnotations?: boolean
}

const defaultConfig: OptionsConfig = {
  mode: "view_only",
  visibility: "public",
  mainColorTable: "",
  userInfo: {
    name: "",
    phone: "",
  },
  colors: {
    editing: true,
    camisa: "",
    gola: "",
    manga: "",
    calcao: "",
    meia: ""
  },
  texts: {
    editing: true,
    add: true,
    content: true,
    textColor: true,
    tableTextColor: "",
    borderEditing: true,
    borderColor: true,
    tableTextBorderColor: "",
    size: true,
    font: true,
    rotation: true,
    delete: true
  },
  logos: {
    editing: true,
    colorTable: "",
    add: true,
    replace: true,
    scale: true,
    rotation: true,
    delete: true
  },
  layers: {
    editing: true,
    colorTable: "",
    simulator: true,
    scale: true,
    rotation: true
  },
  plus: {
    editing: true,
    clear: true,
  },
  piecesVisibility: {
    avatar: true,
    camisa: true,
    short: true,
    meia: true,
  },
  landing: false,
  approvalClientName: "",
  approvalClientEmail: "",
  customTitle: "",
  customSubtitle: "",
  bgColor: "",
  bgImageUrl: "",
  logoUrl: "",
  cardColor: "",
  showFabrics: true,
  showAnnotations: true,
}

export interface ColorOption {
  value: string
  label: string
}

const defaultColorOptions: ColorOption[] = []

// Função auxiliar para adicionar novas cores
export function addColorOptions(
  existingColors: ColorOption[],
  newColors: ColorOption[]
): ColorOption[] {
  const existingValues = new Set(existingColors.map((c) => c.value))
  const uniqueNewColors = newColors.filter((c) => !existingValues.has(c.value))
  return [...existingColors, ...uniqueNewColors]
}

// Função auxiliar para criar uma nova opção de cor
export function createColorOption(value: string, label: string): ColorOption { return { value, label } }

interface OptionsControlModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialConfig?: OptionsConfig
  onSave: (config: OptionsConfig) => void
  colorOptions?: ColorOption[]
  showBranding?: boolean
}

// Função para garantir que a config tenha todos os campos preenchidos
function mergeWithDefaults(config: Partial<OptionsConfig> | undefined): OptionsConfig {
  if (!config) return defaultConfig
  return {
    mode: config.mode ?? defaultConfig.mode,
    mainColorTable: config.mainColorTable ?? defaultConfig.mainColorTable,
    visibility: config.visibility ?? defaultConfig.visibility,
    userInfo: {
      name: config.userInfo?.name ?? defaultConfig.userInfo.name,
      phone: config.userInfo?.phone ?? defaultConfig.userInfo.phone,
    },
    colors: {
      editing: config.colors?.editing ?? defaultConfig.colors.editing,
      camisa: config.colors?.camisa ?? defaultConfig.colors.camisa,
      gola: config.colors?.gola ?? defaultConfig.colors.gola,
      manga: config.colors?.manga ?? defaultConfig.colors.manga,
      calcao: config.colors?.calcao ?? defaultConfig.colors.calcao,
      meia: config.colors?.meia ?? defaultConfig.colors.meia,
    },
    texts: {
      editing: config.texts?.editing ?? defaultConfig.texts.editing,
      add: config.texts?.add ?? defaultConfig.texts.add,
      content: config.texts?.content ?? defaultConfig.texts.content,
      textColor: config.texts?.textColor ?? defaultConfig.texts.textColor,
      tableTextColor: config.texts?.tableTextColor ?? defaultConfig.texts.tableTextColor,
      borderEditing: config.texts?.borderEditing ?? defaultConfig.texts.borderEditing,
      borderColor: config.texts?.borderColor ?? defaultConfig.texts.borderColor,
      tableTextBorderColor: config.texts?.tableTextBorderColor ?? defaultConfig.texts.tableTextBorderColor,
      size: config.texts?.size ?? defaultConfig.texts.size,
      font: config.texts?.font ?? defaultConfig.texts.font,
      rotation: config.texts?.rotation ?? defaultConfig.texts.rotation,
      delete: config.texts?.delete ?? defaultConfig.texts.delete,
    },
    logos: {
      editing: config.logos?.editing ?? defaultConfig.logos.editing,
      colorTable: config.logos?.colorTable ?? defaultConfig.logos.colorTable,
      add: config.logos?.add ?? defaultConfig.logos.add,
      replace: config.logos?.replace ?? defaultConfig.logos.replace,
      scale: config.logos?.scale ?? defaultConfig.logos.scale,
      rotation: config.logos?.rotation ?? defaultConfig.logos.rotation,
      delete: config.logos?.delete ?? defaultConfig.logos.delete,
    },
    layers: {
      editing: config.layers?.editing ?? defaultConfig.layers.editing,
      colorTable: config.layers?.colorTable ?? defaultConfig.layers.colorTable,
      simulator: config.layers?.simulator ?? defaultConfig.layers.simulator,
      scale: config.layers?.scale ?? defaultConfig.layers.scale,
      rotation: config.layers?.rotation ?? defaultConfig.layers.rotation,
    },
    plus: {
      editing: config.plus?.editing ?? defaultConfig.plus.editing,
      clear: config.plus?.clear ?? defaultConfig.plus.clear,
    },
    piecesVisibility: {
      avatar: config.piecesVisibility?.avatar ?? defaultConfig.piecesVisibility.avatar,
      camisa: config.piecesVisibility?.camisa ?? defaultConfig.piecesVisibility.camisa,
      short: config.piecesVisibility?.short ?? defaultConfig.piecesVisibility.short,
      meia: config.piecesVisibility?.meia ?? defaultConfig.piecesVisibility.meia,
    },
    landing: config.landing ?? defaultConfig.landing,
    approvalClientName: config.approvalClientName ?? defaultConfig.approvalClientName,
    approvalClientEmail: config.approvalClientEmail ?? defaultConfig.approvalClientEmail,
    customTitle: config.customTitle ?? defaultConfig.customTitle,
    customSubtitle: config.customSubtitle ?? defaultConfig.customSubtitle,
    bgColor: config.bgColor ?? defaultConfig.bgColor,
    bgImageUrl: config.bgImageUrl ?? defaultConfig.bgImageUrl,
    logoUrl: config.logoUrl ?? defaultConfig.logoUrl,
    cardColor: config.cardColor ?? defaultConfig.cardColor,
    showFabrics: config.showFabrics ?? defaultConfig.showFabrics,
    showAnnotations: config.showAnnotations ?? defaultConfig.showAnnotations,
  }
}

export function OptionsControlModal({
  open,
  onOpenChange,
  initialConfig,
  onSave,
  colorOptions = defaultColorOptions,
  showBranding = false,
}: OptionsControlModalProps) {
  const [config, setConfig] = React.useState<OptionsConfig>(() => mergeWithDefaults(initialConfig))
  const [sharedUrl, setSharedUrl] = React.useState("")
  const [copied, setCopied] = React.useState(false)

  React.useEffect(() => {
    if (open) {
      setConfig(mergeWithDefaults(initialConfig))
    }
  }, [open, initialConfig])

  const isDisabled = config.mode !== "custom_experience"

  // Check if section editing is disabled
  const isColorsDisabled = isDisabled || !config.colors.editing
  const isTextsDisabled = isDisabled || !config.texts.editing
  const isLogosDisabled = isDisabled || !config.logos.editing
  const isLayersDisabled = isDisabled || !config.layers.editing

  const handleModeChange = (value: OptionMode) => { setConfig((prev) => ({ ...prev, mode: value })) }

  const handleVisibilityChange = (value: VisibilityMode) => {
    setConfig((prev) => ({
      ...prev,
      visibility: value,
      userInfo: value === "public" ? { name: "", phone: "" } : prev.userInfo
    }))
  }

  const handleMainTableChange = (value: MainColorTable) => {
    setConfig((prev) => ({ ...prev, mainColorTable: value }));
  };

  const handleUserInfoChange = (field: keyof UserInfo, value: string) => {
    setConfig((prev) => ({
      ...prev,
      userInfo: { ...prev.userInfo, [field]: value },
    }))
  }

  const handleColorChange = <K extends keyof ColorsOptions>(
    key: K,
    value: ColorsOptions[K]
  ) => {
    setConfig((prev) => ({
      ...prev,
      colors: { ...prev.colors, [key]: value },
    }))
  }

  const handleTextChange = <K extends keyof TextOptions>(
    key: K,
    value: TextOptions[K]
  ) => {
    setConfig((prev) => ({
      ...prev,
      texts: { ...prev.texts, [key]: value },
    }))
  }

  const handleLogoChange = <K extends keyof LogosOptions>(
    key: K,
    value: LogosOptions[K]
  ) => {
    setConfig((prev) => ({
      ...prev,
      logos: { ...prev.logos, [key]: value },
    }))
  }

  const handleLayerChange = <K extends keyof LayersOptions>(
    key: K,
    value: LayersOptions[K]
  ) => {
    setConfig((prev) => ({
      ...prev,
      layers: { ...prev.layers, [key]: value },
    }))
  }

  const handlePlusChange = <K extends keyof PlusOptions>(
    key: K,
    value: PlusOptions[K]
  ) => {
    setConfig((prev) => ({
      ...prev,
      plus: { ...prev.plus, [key]: value },
    }))
  }

  const handlePiecesVisibilityChange = <K extends keyof PiecesVisibilityOptions>(
    key: K,
    value: PiecesVisibilityOptions[K]
  ) => {
    setConfig((prev) => ({
      ...prev,
      piecesVisibility: { ...prev.piecesVisibility, [key]: value },
    }))
  }

  const handleSave = () => {
    onSave(config)
    onOpenChange(false)
  }

  const handleShare = async () => {

    const share1 = document.querySelectorAll(".share-1");
    const share2 = document.querySelectorAll(".share-2");
    const shareButton = document.querySelectorAll(".btn-share-link");

    // esconder share1 e botão
    share1.forEach((el) => { (el as HTMLElement).style.display = "none"; });

    shareButton.forEach((el) => { (el as HTMLElement).style.display = "none"; });

    // mostrar share2
    share2.forEach((el) => {
      (el as HTMLElement).style.display = "flex";
      (el as HTMLElement).style.flexDirection = "column";
      (el as HTMLElement).style.alignItems = "center";
    });

    var isForm = 0;

    if (!(window as any).projectSharedID) {

      /*const url = "https://" +
        window.location.host +
        "/share?i=" + localStorage.getItem("user_id");

      (window as any)["appendQRCode"](url);

      return;*/

      (window as any).projectSharedID = 0 + Math.floor(Math.random() * 10000000000000000000);

      isForm = 1;
    }

    const data = await supbaseManager("insert", "iframe_options",
      {
        project_id: (window as any).projectSharedID,
        user_id: getUserId(),
        options: JSON.stringify(config)
      });

    if ((window as any).categoryShared) { (window as any).projectSharedID = (window as any).categoryShared; }

    const url = "https://" +
      window.location.host +
      "/share?i=" + (window as any).projectSharedID + "&n=" + data.id + "&f=" + isForm;

    /*const url = "https://" +
    window.location.host +
    "/?i=" + (window as any).projectSharedID;*/

    (window as any)["appendQRCode"](url);

    setSharedUrl(url);
    setCopied(false);

    (window as any).categoryShared = false;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Controle de Compartilhamento</DialogTitle>
          <DialogDescription>
            Configure as permissões de personalização do link para os clientes.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="h-[60vh] pr-4">
          <div className="space-y-6 hidden share-2" id="share-2">
            {/* QRCODE Selection */}
            <div className="w-full h-[400px] qrcodeNew" id="qrcodeNew"></div>
            {sharedUrl && (
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={sharedUrl}
                  className="text-xs text-muted-foreground flex-1 font-mono"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(sharedUrl)
                    setCopied(true)
                    setTimeout(() => setCopied(false), 2000)
                  }}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  <span className="text-xs">{copied ? "Copiado!" : "Copiar link"}</span>
                </Button>
              </div>
            )}
          </div>
          <div className="space-y-6 share-1" id="share-1">
            {/* Mode Selection */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Modo de Visualização</Label>
              <RadioGroup
                value={config.mode}
                onValueChange={(value) => handleModeChange(value as OptionMode)}
                className="space-y-2"
              >
                <div className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                  <RadioGroupItem value="view_only" id="view_only" />
                  <Label htmlFor="view_only" className="cursor-pointer flex-1">
                    <span className="font-medium">Visualizar apenas</span>
                    <p className="text-sm text-muted-foreground">
                      Desabilita todas as opções de edição
                    </p>
                  </Label>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                  <RadioGroupItem value="full_customization" id="full_customization" />
                  <Label htmlFor="full_customization" className="cursor-pointer flex-1">
                    <span className="font-medium">Customização completa</span>
                    <p className="text-sm text-muted-foreground">
                      Todas as opções habilitadas automaticamente
                    </p>
                  </Label>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                  <RadioGroupItem value="custom_experience" id="custom_experience" />
                  <Label htmlFor="custom_experience" className="cursor-pointer flex-1">
                    <span className="font-medium">Experiência customizada</span>
                    <p className="text-sm text-muted-foreground">
                      Edite as opções abaixo manualmente
                    </p>
                  </Label>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                  <RadioGroupItem value="approval" id="approval" />
                  <Label htmlFor="approval" className="cursor-pointer flex-1">
                    <span className="font-medium">Aprovação de arte</span>
                    <p className="text-sm text-muted-foreground">
                      Cliente só visualiza e assina um comprovante de aceite (sem orçamento)
                    </p>
                  </Label>
                </div>
              </RadioGroup>

              {config.mode === "approval" && (
                <div className="space-y-3 pl-2 pt-1">
                  <div className="space-y-1.5">
                    <Label htmlFor="approval-client-name">Nome do cliente que vai aprovar</Label>
                    <Input
                      id="approval-client-name"
                      value={config.approvalClientName ?? ""}
                      onChange={(e) => setConfig((prev) => ({ ...prev, approvalClientName: e.target.value }))}
                      placeholder="Ex: João Silva"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="approval-client-email">E-mail do cliente (opcional)</Label>
                    <Input
                      id="approval-client-email"
                      type="email"
                      value={config.approvalClientEmail ?? ""}
                      onChange={(e) => setConfig((prev) => ({ ...prev, approvalClientEmail: e.target.value }))}
                      placeholder="cliente@email.com"
                    />
                  </div>
                </div>
              )}
            </div>
            <Separator />
            {/* Content Visibility Section */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Conteúdo do Compartilhamento</Label>
              <div className="space-y-3 pl-2">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="show-fabrics"
                    checked={config.showFabrics !== false}
                    onCheckedChange={(checked) =>
                      setConfig((prev) => ({ ...prev, showFabrics: checked === true }))
                    }
                  />
                  <Label htmlFor="show-fabrics" className="cursor-pointer">
                    Mostrar Tecidos
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="show-annotations"
                    checked={config.showAnnotations !== false}
                    onCheckedChange={(checked) =>
                      setConfig((prev) => ({ ...prev, showAnnotations: checked === true }))
                    }
                  />
                  <Label htmlFor="show-annotations" className="cursor-pointer">
                    Mostrar Anotações
                  </Label>
                </div>
              </div>
            </div>
            <Separator />
            {/* Main Color Table Section */}
            <div className={`flex items-center justify-between`}>
              <Label htmlFor="layers-colorTable">Tabela de Cores Principal</Label>
              <Select
                value={config.mainColorTable}
                onValueChange={(value) =>
                  handleMainTableChange(value)
                }
              >
                <SelectTrigger id="mainColorTable" className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {colorOptions.map((color) => (
                    <SelectItem key={color.value} value={color.value}>
                      {color.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Separator />
            {/* Visibility Section */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Visibilidade do Link</Label>
              <RadioGroup
                value={config.visibility}
                onValueChange={(value) => handleVisibilityChange(value as VisibilityMode)}
                className="space-y-2"
              >
                <div className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                  <RadioGroupItem value="public" id="visibility_public" />
                  <Label htmlFor="visibility_public" className="cursor-pointer flex-1">
                    <span className="font-medium">Público</span>
                    <p className="text-sm text-muted-foreground">
                      Qualquer pessoa com o link pode acessar
                    </p>
                  </Label>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-accent/50 transition-colors">
                  <RadioGroupItem value="private" id="visibility_private" />
                  <Label htmlFor="visibility_private" className="cursor-pointer flex-1">
                    <span className="font-medium">Privado</span>
                    <p className="text-sm text-muted-foreground">
                      Link enviado para um usuário específico
                    </p>
                  </Label>
                </div>
              </RadioGroup>
              {config.visibility === "private" && (
                <div className="space-y-3 pt-2 pl-2">
                  <div className="space-y-2">
                    <Label htmlFor="user-name">Nome do usuário</Label>
                    <Input
                      id="user-name"
                      placeholder="Digite o nome"
                      value={config.userInfo.name}
                      onChange={(e) => handleUserInfoChange("name", e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user-phone">Telefone</Label>
                    <Input
                      id="user-phone"
                      placeholder="(00) 00000-0000"
                      value={config.userInfo.phone}
                      onChange={(e) => handleUserInfoChange("phone", e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
            <Separator />
            {/* Landing Section 
            <div className="space-y-3">
              <Label className="text-base font-semibold">Habilitar Site</Label>
              <div className="space-y-3 pl-2">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="check-landing"
                    checked={config.landing}
                    onCheckedChange={(checked) => {
                      setConfig((prev) => ({
                        ...prev,
                        landing: checked === true,
                      }))
                    }}
                  />
                  <Label htmlFor="check-landing" className="cursor-pointer">
                    Habilitar
                  </Label>
                </div>
              </div>
            </div>*/}
            {/* ── Branding / Personalização visual ──────────────────────────── */}
            {showBranding && (
            <div className="space-y-3">
              <Label className="text-base font-semibold">Aparência do Compartilhamento</Label>
              <p className="text-sm text-muted-foreground">
                Personalize a visualização do link compartilhado (cores de fundo). A logo é puxada automaticamente dos dados da loja.
              </p>
              <div className="space-y-3 pl-2">
                {/* Cor de fundo */}
                <div className="flex items-center justify-between">
                  <Label htmlFor="bg-color">Cor de Fundo</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      id="bg-color"
                      value={config.bgColor || "#0b0d12"}
                      onChange={(e) => setConfig((prev) => ({ ...prev, bgColor: e.target.value }))}
                      className="w-8 h-8 rounded border border-border cursor-pointer"
                    />
                    {config.bgColor && (
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-destructive"
                        onClick={() => setConfig((prev) => ({ ...prev, bgColor: "" }))}
                      >
                        Resetar
                      </button>
                    )}
                  </div>
                </div>
                {/* Cor dos cards */}
                <div className="flex items-center justify-between">
                  <Label htmlFor="card-color">Cor dos Cards</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      id="card-color"
                      value={config.cardColor || "#16181d"}
                      onChange={(e) => setConfig((prev) => ({ ...prev, cardColor: e.target.value }))}
                      className="w-8 h-8 rounded border border-border cursor-pointer"
                    />
                    {config.cardColor && (
                      <button
                        type="button"
                        className="text-xs text-muted-foreground hover:text-destructive"
                        onClick={() => setConfig((prev) => ({ ...prev, cardColor: "" }))}
                      >
                        Resetar
                      </button>
                    )}
                  </div>
                </div>
                {/* Imagem de fundo */}
                <div className="space-y-1.5">
                  <Label>Imagem de Fundo</Label>
                  <div className="flex items-center gap-2">
                    {config.bgImageUrl && (
                      <img
                        src={config.bgImageUrl}
                        alt="Fundo"
                        className="w-16 h-10 rounded object-cover border border-border"
                      />
                    )}
                    <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-border cursor-pointer hover:bg-accent/50 transition-colors text-sm">
                      <Upload className="w-4 h-4" />
                      <span>{config.bgImageUrl ? "Trocar imagem" : "Carregar imagem"}</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          const ext = file.name.split(".").pop() || "webp";
                          const filename = `share-bg-${getUserId()}-${Date.now()}.${ext}`;
                          const { data, error } = await supabase.storage
                            .from("images")
                            .upload(filename, file, { contentType: file.type, upsert: true });
                          if (!error) {
                            const { data: urlData } = supabase.storage
                              .from("images")
                              .getPublicUrl(filename);
                            setConfig((prev) => ({ ...prev, bgImageUrl: urlData.publicUrl }));
                          }
                        }}
                      />
                    </label>
                    {config.bgImageUrl && (
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-destructive transition-colors"
                        onClick={() => setConfig((prev) => ({ ...prev, bgImageUrl: "" }))}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            )}
            <Separator />
            {/* Pieces Visibility Section */}
            <div className="space-y-3">
              <Label className="text-base font-semibold">Visibilidade das Peças</Label>
              <div className="space-y-3 pl-2">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="pieces-avatar"
                    checked={config.piecesVisibility.avatar}
                    onCheckedChange={(checked) =>
                      handlePiecesVisibilityChange("avatar", checked === true)
                    }
                  />
                  <Label htmlFor="pieces-avatar" className="cursor-pointer">
                    Avatar
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="pieces-camisa"
                    checked={config.piecesVisibility.camisa}
                    onCheckedChange={(checked) =>
                      handlePiecesVisibilityChange("camisa", checked === true)
                    }
                  />
                  <Label htmlFor="pieces-camisa" className="cursor-pointer">
                    Camisa
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="pieces-short"
                    checked={config.piecesVisibility.short}
                    onCheckedChange={(checked) =>
                      handlePiecesVisibilityChange("short", checked === true)
                    }
                  />
                  <Label htmlFor="pieces-short" className="cursor-pointer">
                    Short
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="pieces-meia"
                    checked={config.piecesVisibility.meia}
                    onCheckedChange={(checked) =>
                      handlePiecesVisibilityChange("meia", checked === true)
                    }
                  />
                  <Label htmlFor="pieces-meia" className="cursor-pointer">
                    Meia
                  </Label>
                </div>
              </div>
            </div>
            <Separator />
            {/* Colors Section */}
            <div className={`space-y-4 ${isDisabled ? "opacity-50" : ""}`}>
              <Label className="text-base font-semibold">Cores</Label>
              <div className="space-y-3 pl-2">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="colors-editing"
                    checked={config.colors.editing}
                    onCheckedChange={(checked) =>
                      handleColorChange("editing", checked === true)
                    }
                    disabled={isDisabled}
                  />
                  <Label htmlFor="colors-editing" className="cursor-pointer">
                    Edição
                  </Label>
                </div>
                <div className={`flex items-center justify-between ${isColorsDisabled ? "opacity-50" : ""}`}>
                  <Label htmlFor="colors-camisa">Camisa</Label>
                  <Select
                    value={config.colors.camisa}
                    onValueChange={(value) => handleColorChange("camisa", value)}
                    disabled={isColorsDisabled}
                  >
                    <SelectTrigger id="colors-camisa" className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          {color.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className={`flex items-center justify-between ${isColorsDisabled ? "opacity-50" : ""}`}>
                  <Label htmlFor="colors-gola">Gola</Label>
                  <Select
                    value={config.colors.gola}
                    onValueChange={(value) => handleColorChange("gola", value)}
                    disabled={isColorsDisabled}
                  >
                    <SelectTrigger id="colors-gola" className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          {color.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className={`flex items-center justify-between ${isColorsDisabled ? "opacity-50" : ""}`}>
                  <Label htmlFor="colors-manga">Manga</Label>
                  <Select
                    value={config.colors.manga}
                    onValueChange={(value) => handleColorChange("manga", value)}
                    disabled={isColorsDisabled}
                  >
                    <SelectTrigger id="colors-manga" className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          {color.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className={`flex items-center justify-between ${isColorsDisabled ? "opacity-50" : ""}`}>
                  <Label htmlFor="colors-calcao">Short</Label>
                  <Select
                    value={config.colors.calcao}
                    onValueChange={(value) => handleColorChange("calcao", value)}
                    disabled={isColorsDisabled}
                  >
                    <SelectTrigger id="colors-calcao" className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          {color.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className={`flex items-center justify-between ${isColorsDisabled ? "opacity-50" : ""}`}>
                  <Label htmlFor="colors-meia">Meião</Label>
                  <Select
                    value={config.colors.meia}
                    onValueChange={(value) => handleColorChange("meia", value)}
                    disabled={isColorsDisabled}
                  >
                    <SelectTrigger id="colors-meia" className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          {color.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <Separator />
            {/* Texts Section */}
            <div className={`space-y-4 ${isDisabled ? "opacity-50" : ""}`}>
              <Label className="text-base font-semibold">Textos</Label>
              <div className="space-y-3 pl-2">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="texts-editing"
                    checked={config.texts.editing}
                    onCheckedChange={(checked) =>
                      handleTextChange("editing", checked === true)
                    }
                    disabled={isDisabled}
                  />
                  <Label htmlFor="texts-editing" className="cursor-pointer">
                    Edição
                  </Label>
                </div>
                <div className={`flex items-center gap-3 ${isTextsDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="texts-add"
                    checked={config.texts.add}
                    onCheckedChange={(checked) =>
                      handleTextChange("add", checked === true)
                    }
                    disabled={isTextsDisabled}
                  />
                  <Label htmlFor="texts-add" className="cursor-pointer">
                    Adicionar
                  </Label>
                </div>
                <div className={`flex items-center gap-3 ${isTextsDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="texts-content"
                    checked={config.texts.content}
                    onCheckedChange={(checked) =>
                      handleTextChange("content", checked === true)
                    }
                    disabled={isTextsDisabled}
                  />
                  <Label htmlFor="texts-content" className="cursor-pointer">
                    Conteúdo
                  </Label>
                </div>
                <div className={`flex items-center gap-3 ${isTextsDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="texts-textColor"
                    checked={config.texts.textColor}
                    onCheckedChange={(checked) =>
                      handleTextChange("textColor", checked === true)
                    }
                    disabled={isTextsDisabled}
                  />
                  <Label htmlFor="texts-textColor" className="cursor-pointer">
                    Cor do texto
                  </Label>
                </div>
                <div className={`flex items-center justify-between ${isTextsDisabled ? "opacity-50" : ""}`}>
                  <Label htmlFor="texts-tableTextColor">Cor do texto tabela</Label>
                  <Select
                    value={config.texts.tableTextColor}
                    onValueChange={(value) =>
                      handleTextChange("tableTextColor", value)
                    }
                    disabled={isTextsDisabled}
                  >
                    <SelectTrigger id="texts-tableTextColor" className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          {color.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className={`flex items-center gap-3 ${isTextsDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="texts-borderEditing"
                    checked={config.texts.borderEditing}
                    onCheckedChange={(checked) =>
                      handleTextChange("borderEditing", checked === true)
                    }
                    disabled={isTextsDisabled}
                  />
                  <Label htmlFor="texts-borderEditing" className="cursor-pointer">
                    Borda edição
                  </Label>
                </div>
                <div className={`flex items-center gap-3 ${isTextsDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="texts-borderColor"
                    checked={config.texts.borderColor}
                    onCheckedChange={(checked) =>
                      handleTextChange("borderColor", checked === true)
                    }
                    disabled={isTextsDisabled}
                  />
                  <Label htmlFor="texts-borderColor" className="cursor-pointer">
                    Borda cor
                  </Label>
                </div>
                <div className={`flex items-center justify-between ${isTextsDisabled ? "opacity-50" : ""}`}>
                  <Label htmlFor="texts-tableTextBorderColor">Cor borda do texto tabela</Label>
                  <Select
                    value={config.texts.tableTextBorderColor}
                    onValueChange={(value) =>
                      handleTextChange("tableTextBorderColor", value)
                    }
                    disabled={isTextsDisabled}
                  >
                    <SelectTrigger id="texts-tableTextBorderColor" className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          {color.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className={`flex items-center gap-3 ${isTextsDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="texts-size"
                    checked={config.texts.size}
                    onCheckedChange={(checked) =>
                      handleTextChange("size", checked === true)
                    }
                    disabled={isTextsDisabled}
                  />
                  <Label htmlFor="texts-size" className="cursor-pointer">
                    Tamanho
                  </Label>
                </div>
                <div className={`flex items-center gap-3 ${isTextsDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="texts-font"
                    checked={config.texts.font}
                    onCheckedChange={(checked) =>
                      handleTextChange("font", checked === true)
                    }
                    disabled={isTextsDisabled}
                  />
                  <Label htmlFor="texts-font" className="cursor-pointer">
                    Fonte
                  </Label>
                </div>
                <div className={`flex items-center gap-3 ${isTextsDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="texts-rotation"
                    checked={config.texts.rotation}
                    onCheckedChange={(checked) =>
                      handleTextChange("rotation", checked === true)
                    }
                    disabled={isTextsDisabled}
                  />
                  <Label htmlFor="texts-rotation" className="cursor-pointer">
                    Rotação
                  </Label>
                </div>
                <div className={`flex items-center gap-3 ${isTextsDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="texts-delete"
                    checked={config.texts.delete}
                    onCheckedChange={(checked) =>
                      handleTextChange("delete", checked === true)
                    }
                    disabled={isTextsDisabled}
                  />
                  <Label htmlFor="texts-delete" className="cursor-pointer">
                    Deletar
                  </Label>
                </div>
              </div>
            </div>
            <Separator />
            {/* Logos Section */}
            <div className={`space-y-4 ${isDisabled ? "opacity-50" : ""}`}>
              <Label className="text-base font-semibold">Logos</Label>

              <div className="space-y-3 pl-2">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="logos-editing"
                    checked={config.logos.editing}
                    onCheckedChange={(checked) =>
                      handleLogoChange("editing", checked === true)
                    }
                    disabled={isDisabled}
                  />
                  <Label htmlFor="logos-editing" className="cursor-pointer">
                    Edição
                  </Label>
                </div>
                <div className={`flex items-center justify-between ${isTextsDisabled ? "opacity-50" : ""}`}>
                  <Label htmlFor="texts-colorTable">Cor do Logo Tabela</Label>
                  <Select
                    value={config.logos.colorTable}
                    onValueChange={(value) =>
                      handleLogoChange("colorTable", value)
                    }
                    disabled={isTextsDisabled}
                  >
                    <SelectTrigger id="logos-colorTable" className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          {color.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className={`flex items-center gap-3 ${isLogosDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="logos-add"
                    checked={config.logos.add}
                    onCheckedChange={(checked) =>
                      handleLogoChange("add", checked === true)
                    }
                    disabled={isLogosDisabled}
                  />
                  <Label htmlFor="logos-add" className="cursor-pointer">
                    Adicionar
                  </Label>
                </div>
                <div className={`flex items-center gap-3 ${isLogosDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="logos-replace"
                    checked={config.logos.replace}
                    onCheckedChange={(checked) =>
                      handleLogoChange("replace", checked === true)
                    }
                    disabled={isLogosDisabled}
                  />
                  <Label htmlFor="logos-replace" className="cursor-pointer">
                    Substituir
                  </Label>
                </div>
                <div className={`flex items-center gap-3 ${isLogosDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="logos-scale"
                    checked={config.logos.scale}
                    onCheckedChange={(checked) =>
                      handleLogoChange("scale", checked === true)
                    }
                    disabled={isLogosDisabled}
                  />
                  <Label htmlFor="logos-scale" className="cursor-pointer">
                    Escala
                  </Label>
                </div>
                <div className={`flex items-center gap-3 ${isLogosDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="logos-rotation"
                    checked={config.logos.rotation}
                    onCheckedChange={(checked) =>
                      handleLogoChange("rotation", checked === true)
                    }
                    disabled={isLogosDisabled}
                  />
                  <Label htmlFor="logos-rotation" className="cursor-pointer">
                    Rotação
                  </Label>
                </div>
                <div className={`flex items-center gap-3 ${isLogosDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="logos-delete"
                    checked={config.logos.delete}
                    onCheckedChange={(checked) =>
                      handleLogoChange("delete", checked === true)
                    }
                    disabled={isLogosDisabled}
                  />
                  <Label htmlFor="logos-delete" className="cursor-pointer">
                    Deletar
                  </Label>
                </div>
              </div>
            </div>
            <Separator />
            {/* Layers Section */}
            <div className={`space-y-4 ${isDisabled ? "opacity-50" : ""}`}>
              <Label className="text-base font-semibold">Layers</Label>
              <div className="space-y-3 pl-2">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="layers-editing"
                    checked={config.layers.editing}
                    onCheckedChange={(checked) =>
                      handleLayerChange("editing", checked === true)
                    }
                    disabled={isDisabled}
                  />
                  <Label htmlFor="layers-editing" className="cursor-pointer">
                    Edição
                  </Label>
                </div>
                <div className={`flex items-center justify-between ${isTextsDisabled ? "opacity-50" : ""}`}>
                  <Label htmlFor="layers-colorTable">Cor do Layer Tabela</Label>
                  <Select
                    value={config.layers.colorTable}
                    onValueChange={(value) =>
                      handleLayerChange("colorTable", value)
                    }
                    disabled={isTextsDisabled}
                  >
                    <SelectTrigger id="layers-colorTable" className="w-[150px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {colorOptions.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          {color.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className={`flex items-center gap-3 ${isLayersDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="layers-simulator"
                    checked={config.layers.simulator}
                    onCheckedChange={(checked) =>
                      handleLayerChange("simulator", checked === true)
                    }
                    disabled={isLayersDisabled}
                  />
                  <Label htmlFor="layers-simulator" className="cursor-pointer">
                    Simulador
                  </Label>
                </div>
                <div className={`flex items-center gap-3 ${isLayersDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="layers-scale"
                    checked={config.layers.scale}
                    onCheckedChange={(checked) =>
                      handleLayerChange("scale", checked === true)
                    }
                    disabled={isLayersDisabled}
                  />
                  <Label htmlFor="layers-scale" className="cursor-pointer">
                    Escala
                  </Label>
                </div>
                <div className={`flex items-center gap-3 ${isLayersDisabled ? "opacity-50" : ""}`}>
                  <Checkbox
                    id="layers-rotation"
                    checked={config.layers.rotation}
                    onCheckedChange={(checked) =>
                      handleLayerChange("rotation", checked === true)
                    }
                    disabled={isLayersDisabled}
                  />
                  <Label htmlFor="layers-rotation" className="cursor-pointer">
                    Rotação
                  </Label>
                </div>
              </div>
            </div>
            <Separator />
          </div>
        </ScrollArea>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          <Button id="btn-share-link" className="btn-share-link" onClick={handleShare}>Gerar Link</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export { defaultConfig }